import type { MarketingTranslator } from '@/i18n/marketing'
import { useTranslation } from 'react-i18next'
import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { ChevronRight, Menu, X } from 'lucide-react'
import {
  BriefingApplicationModal,
  BriefingApplicationManagerModal,
  FranchiseInquiryModal,
} from '@marketing/components'

function getNavItems(mt: MarketingTranslator) {
  const NAV_ITEMS = [
    { to: '/about/quebon', label: mt('header.about') },
    { to: '/about/lc', label: mt('header.center') },
    { to: '/franchise', label: mt('header.franchise') },
    { to: '/reviews', label: mt('header.reviews') },
    { to: '/announcements', label: mt('header.announcements') },
  ]
  return NAV_ITEMS
}

const openBizInfo = () => {
  window.open(
    'http://www.ftc.go.kr/bizCommPop.do?wrkr_no=4268700450',
    'bizCommPop',
    'width=750,height=700'
  )
}

type FooterLink =
  | { label: string; href: string }
  | { label: string; onClick: () => void }

function getFooterLinks(mt: MarketingTranslator) {
  const FOOTER_LINKS: FooterLink[] = [
    { label: mt('footer.company'), href: 'https://www.quebon.tv/about' },
    {
      label: mt('footer.careers'),
      href: 'https://quebon.notion.site/36e295b594ac4d44ad2390393f197f13',
    },
    { label: mt('footer.businessInfo'), onClick: openBizInfo },
  ]
  return FOOTER_LINKS
}

export default function BrandHeader() {
  const { t: mt, i18n } = useTranslation('marketing')
  const FOOTER_LINKS = getFooterLinks(mt)
  const isEnglish = i18n.resolvedLanguage === 'en'
  const NAV_ITEMS = getNavItems(mt).filter(item => !isEnglish || !['/reviews', '/announcements'].includes(item.to))
  // 한국어는 원래 데스크톱 메뉴 간격(gap-8)과 md 분기점을 유지하고,
  // 라벨이 긴 영어만 좁은 간격(gap-4)과 lg 분기점을 쓴다.
  const desktopNavClass = isEnglish ? 'gap-4 lg:flex' : 'gap-8 md:flex'
  const menuButtonClass = isEnglish ? 'lg:hidden' : 'md:hidden'

  const location = useLocation()
  const [hasScrolled, setHasScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [animating, setAnimating] = useState(false)
  const [briefingOpen, setBriefingOpen] = useState(false)
  const [inquiryOpen, setInquiryOpen] = useState(false)
  const [manageOpen, setManageOpen] = useState(false)
  const animTimer = useRef<number | null>(null)

  // 스크롤 감지
  useEffect(() => {
    const onScroll = () => {
      setHasScrolled(window.scrollY > 10)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // 모바일 패널 슬라이드 애니메이션 (열림)
  useEffect(() => {
    if (mobileOpen) {
      // 마운트 직후 다음 tick에 animating=true로 바꿔서 translate 트랜지션 트리거
      const id = window.setTimeout(() => setAnimating(true), 10)
      return () => window.clearTimeout(id)
    }
  }, [mobileOpen])

  // 라우트 변경 시 모바일 메뉴 닫기
  useEffect(() => {
    closePanel()
  }, [location.pathname])

  // 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (animTimer.current !== null) {
        window.clearTimeout(animTimer.current)
      }
    }
  }, [])

  const closePanel = () => {
    setAnimating(false)
    if (animTimer.current !== null) window.clearTimeout(animTimer.current)
    animTimer.current = window.setTimeout(() => {
      setMobileOpen(false)
      animTimer.current = null
    }, 300)
  }

  const headerSkin = hasScrolled
    ? 'bg-white/90 backdrop-blur-[12px] border-gray-100 shadow-sm'
    : 'bg-white border-transparent'

  return (
    <>
      <header
        className={`sticky top-0 z-40 border-b transition-colors ${headerSkin}`}
      >
        <div className="flex justify-between items-center px-6 mx-auto max-w-7xl h-16">
          <Link to="/" aria-label={mt('header.home')} className="flex items-center">
            <img
              src="/quebon-logo.svg"
              alt={mt('header.brand')}
              className="w-auto h-12"
            />
          </Link>

          <nav className={`hidden items-center ${desktopNavClass}`}>
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-xl tracking-[-0.6px] transition-colors ${isActive
                    ? 'text-[#4599ff] font-semibold'
                    : 'text-[#505050] font-normal hover:text-gray-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <button
            type="button"
            aria-label={mt('header.openMenu')}
            className={`inline-flex justify-center items-center w-12 h-12 text-[#333333] rounded-lg hover:bg-gray-100 ${menuButtonClass}`}
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="w-8 h-8" />
          </button>
        </div>
      </header>

      {mobileOpen && (
        <>
          <div
            className={`fixed inset-0 z-[1000] bg-black/40 transition-opacity duration-300 ${animating ? 'opacity-100' : 'opacity-0'
              }`}
            onClick={closePanel}
            aria-hidden="true"
          />
          <aside
            className={`fixed inset-y-0 right-0 z-[1000] w-[min(360px,90vw)] flex flex-col bg-white shadow-xl transition-transform duration-300 ${animating ? 'translate-x-0' : 'translate-x-full'
              }`}
            role="dialog"
            aria-modal="true"
            aria-label={mt('header.mobileMenu')}
          >
            {/* Header */}
            <div className="flex justify-between items-center px-4 h-20 bg-white/90 backdrop-blur-[12px] shrink-0">
              <Link
                to="/"
                onClick={closePanel}
                aria-label={mt('header.home')}
                className="flex items-center px-2 py-2"
              >
                <img
                  src="/quebon-logo.svg"
                  alt={mt('header.brand')}
                  className="w-auto h-12"
                />
              </Link>
              <button
                type="button"
                aria-label={mt('header.closeMenu')}
                className="inline-flex justify-center items-center w-12 h-12 text-[#333333] rounded-lg hover:bg-gray-100"
                onClick={closePanel}
              >
                <X className="w-8 h-8" strokeWidth={1.5} />
              </button>
            </div>

            {/* Body */}
            <div className="flex overflow-y-auto flex-col flex-1 bg-white">
              <nav className="flex flex-col gap-4 pt-4 pb-6">
                {NAV_ITEMS.map((item, idx) => (
                  <Fragment key={item.to}>
                    <NavLink
                      to={item.to}
                      onClick={closePanel}
                      className={({ isActive }) =>
                        `flex items-center justify-between h-6 px-6 text-[18px] font-medium tracking-[-0.6px] transition-colors ${isActive ? 'text-[#4599ff]' : 'text-[#131313]'
                        }`
                      }
                    >
                      <span>{item.label}</span>
                      <ChevronRight
                        className="w-5 h-5 text-[#131313]"
                        strokeWidth={2}
                      />
                    </NavLink>
                    {idx < NAV_ITEMS.length - 1 && (
                      <div className="h-px w-full bg-[#dadada]" />
                    )}
                  </Fragment>
                ))}
              </nav>

              <div className="flex flex-col gap-2 px-6 pb-6 mt-auto">
                <button
                  type="button"
                  onClick={() => {
                    closePanel()
                    setBriefingOpen(true)
                  }}
                  className="flex h-12 items-center justify-center rounded-[12px] bg-[#4599ff] text-white text-[18px] font-medium tracking-[-0.6px] transition-colors hover:bg-[#3380e6]"
                >
                  {mt('header.briefing')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closePanel()
                    setInquiryOpen(true)
                  }}
                  className="flex h-12 items-center justify-center rounded-[12px] bg-[#143d87] text-white text-[18px] font-medium tracking-[-0.6px] transition-colors hover:bg-[#1c52a8]"
                >
                  {mt('franchise.franchiseContent.earlyBird.ctaLabel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closePanel()
                    setManageOpen(true)
                  }}
                  className="flex h-12 items-center justify-center rounded-[12px] border border-[#dadada] text-[#53555a] text-[18px] font-medium tracking-[-0.6px] transition-colors hover:bg-[#f4f4f4]"
                >
                  신청 내역 조회
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-center gap-2 bg-[#f4f4f4] px-6 py-6 shrink-0">
              {FOOTER_LINKS.map((link, idx) => (
                <Fragment key={link.label}>
                  {'href' in link ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-[15px] text-[#53555a] tracking-[-0.6px] hover:opacity-80 transition-opacity"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        closePanel()
                        link.onClick()
                      }}
                      className="text-[15px] text-[#53555a] tracking-[-0.6px] hover:opacity-80 transition-opacity"
                    >
                      {link.label}
                    </button>
                  )}
                  {idx < FOOTER_LINKS.length - 1 && (
                    <span
                      className="h-2.5 w-px bg-[#d4d4d6]"
                      aria-hidden="true"
                    />
                  )}
                </Fragment>
              ))}
            </div>
          </aside>
        </>
      )}

      <BriefingApplicationModal
        open={briefingOpen}
        onClose={() => setBriefingOpen(false)}
      />
      <FranchiseInquiryModal
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
      />
      <BriefingApplicationManagerModal
        open={manageOpen}
        onOpenChange={setManageOpen}
      />
    </>
  )
}
